import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  makeLayoutId,
  savePortalLayout,
  getPortalLayout,
  getAllPortalLayouts,
  deletePortalLayout,
} from "../../services/layoutService";
import { uploadPortalMedia } from "../../services/mediaUploadService";
import MediaPicker from "../../components/builder/MediaPicker";
import {
  refreshTopicMediaStatus,
  topicMediaStatus,
  useTopicMediaData,
} from "../../components/builder/topicMediaStatus";
import {
  STATE_GROUPS,
  stateName,
  AVAILABLE_STATES,
  isScienceReady,
  isMathReady,
} from "../../config/geography";
import "./layoutBuilder.css";
import "./layoutBuilderPro.css";
import "./layoutBuilderLibrary.css";
import TopicLibraryPanel from "./TopicLibraryPanel";
import TopicPicturesPanel from "./TopicPicturesPanel";
import RewardsSettingsPanel from "./RewardsSettingsPanel";
import VoiceSettingsPanel from "./VoiceSettingsPanel";
import GameSoundsPanel from "./GameSoundsPanel";
import TreasureMapPagesPanel from "./TreasureMapPagesPanel";
import SeasonDatesPanel from "./SeasonDatesPanel";
import SingAlongPanel from "./SingAlongPanel";
import LessonVideoPanel from "./LessonVideoPanel";
import VideoUploadPanel from "./VideoUploadPanel";
import GameGeneratorPanel from "./GameGeneratorPanel";
import GameLibraryPanel from "./GameLibraryPanel";
import StateAvailabilityPanel from "./StateAvailabilityPanel";
import SuggestedTopicsPanel from "./SuggestedTopicsPanel";
import HomeDemoPanel from "./HomeDemoPanel";

/* ------------------------------------------------------------------ *
 * Option lists
 * ------------------------------------------------------------------ */

// The builder used to carry its own seven-state list. Signup offers all 51
// jurisdictions (src/config/geography.js), so a parent could register in a
// state the builder had no way to build a page for — and nothing in the UI
// said so. One list now, read from the same file the signup form reads,
// grouped the same way and labelled with the code that ends up in the
// layout ID and the role string.
const STATES = STATE_GROUPS.flatMap((group) =>
  group.codes.map((code) => ({
    label: stateName(code),
    value: code.toLowerCase(),
    code,
    group: group.label,
  }))
);

// Same groups, kept intact so the <select> can render <optgroup>s that match
// what a parent sees at signup.
const STATE_SELECT_GROUPS = STATE_GROUPS.map((group) => ({
  label: group.label,
  options: group.codes.map((code) => ({
    label: `${stateName(code)} (${code})`,
    value: code.toLowerCase(),
  })),
})).filter((group) => group.options.length > 0);

// What the signup dropdown promises for this state, in one word, so the
// library shelf and the signup form can't drift apart silently.
const stateAvailability = (value) => {
  const code = String(value || "").toUpperCase();
  if (AVAILABLE_STATES.includes(code)) return { key: "live", label: "Available now" };
  const science = isScienceReady(code);
  const math = isMathReady(code);
  if (science && math) return { key: "core", label: "Science + math" };
  if (math) return { key: "part", label: "Math only" };
  if (science) return { key: "part", label: "Science only" };
  return { key: "soon", label: "Coming soon" };
};

// Must match GRADE_TOKENS in src/config/geography.js and the generated role
// list in App.js. Tokens are lowercase: the builder previously used "K" while
// layoutService.js lowercases on read, so every Kindergarten layout built here
// was saved to a document ID nobody looks up.
//
// Pre-K is out because no Pre-K standards or layouts exist in any state — the
// option only offered a page that could never be seeded.
const GRADES = ["k", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th"];

const SUBJECTS = [
  { label: "Science", value: "science" },
  { label: "Math", value: "math" },
  { label: "Social Studies", value: "socialstudies" },
  { label: "Other", value: "other" },
];

const VIEWS = [
  { label: "Educator Page", value: "educator" },
  { label: "Student Page", value: "student" },
];

const BONUS_ROUNDS = [
  { label: "Diamond Tap", value: "diamondTap" },
  { label: "Memory Match", value: "memoryMatch" },
  { label: "Match 3", value: "match3" },
  { label: "Treasure Quest (map + match 3)", value: "treasureQuest" },
  { label: "Count Pop", value: "countPop" },
  { label: "Rhythm Tap", value: "rhythmTap" },

  { label: "2 Times Tables", value: "timesTable:2" },
  { label: "3 Times Tables", value: "timesTable:3" },
  { label: "4 Times Tables", value: "timesTable:4" },
  { label: "5 Times Tables", value: "timesTable:5" },
  { label: "6 Times Tables", value: "timesTable:6" },
  { label: "7 Times Tables", value: "timesTable:7" },
  { label: "8 Times Tables", value: "timesTable:8" },
  { label: "9 Times Tables", value: "timesTable:9" },
  { label: "10 Times Tables", value: "timesTable:10" },
  { label: "11 Times Tables", value: "timesTable:11" },
  { label: "12 Times Tables", value: "timesTable:12" },

  { label: "Count by 2s", value: "countBy:2" },
  { label: "Count by 3s", value: "countBy:3" },
  { label: "Count by 4s", value: "countBy:4" },
  { label: "Count by 5s", value: "countBy:5" },
  { label: "Count by 6s", value: "countBy:6" },
  { label: "Count by 7s", value: "countBy:7" },
  { label: "Count by 8s", value: "countBy:8" },
  { label: "Count by 9s", value: "countBy:9" },
  { label: "Count by 10s", value: "countBy:10" },

  { label: "Addition Practice", value: "addition:10" },
  { label: "Subtraction Practice", value: "subtraction:10" },

  { label: "None", value: "none" },
];

const GAME_TYPES = [
  { label: "Multiple Choice", value: "multipleChoice" },
  { label: "Matching", value: "matching" },
  { label: "Drag and Drop", value: "dragDrop" },
  { label: "Match 3", value: "match3" },
  { label: "Memory Match", value: "memoryMatch" },
  { label: "Count Pop", value: "countPop" },
  { label: "Talk Choice", value: "talkChoice" },

  { label: "2 Times Table Game", value: "mathTimesTable:2" },
  { label: "3 Times Table Game", value: "mathTimesTable:3" },
  { label: "4 Times Table Game", value: "mathTimesTable:4" },
  { label: "5 Times Table Game", value: "mathTimesTable:5" },
  { label: "6 Times Table Game", value: "mathTimesTable:6" },
  { label: "7 Times Table Game", value: "mathTimesTable:7" },
  { label: "8 Times Table Game", value: "mathTimesTable:8" },
  { label: "9 Times Table Game", value: "mathTimesTable:9" },
  { label: "10 Times Table Game", value: "mathTimesTable:10" },
  { label: "11 Times Table Game", value: "mathTimesTable:11" },
  { label: "12 Times Table Game", value: "mathTimesTable:12" },

  { label: "Addition Game", value: "mathAddition:10" },
  { label: "Subtraction Game", value: "mathSubtraction:10" },
];

const DEFAULT_TOOLS = {
  video: true,
  music: true,
  quiz: true,
  game: true,
  singalong: true,
  slideshow: false,
};

const TOOL_LABELS = {
  video: "Video",
  music: "Song",
  quiz: "Quiz",
  game: "Game",
  singalong: "Sing-along",
  slideshow: "Slideshow",
};

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

// Every topic carries a private _uid. It never goes to Firestore — it only
// exists so the editor can tell topics apart while you're typing. The old
// build identified topics by slug, and slug is rebuilt from the title on
// every keystroke, so a brand-new (blank) topic had no identity at all and
// a renamed topic changed identity mid-edit. That's what made new topics
// disappear.
let uidCounter = 0;
const makeUid = () =>
  `t_${Date.now().toString(36)}_${(uidCounter++).toString(36)}`;

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const stateLabel = (value) => {
  const found = STATES.find((s) => s.value === value);
  if (found) return found.label;
  // A layout saved before the list was unified, or a code that isn't a state
  // at all. stateName() returns the code itself when it doesn't recognise it,
  // so an orphan document still renders instead of vanishing.
  return stateName(value) || String(value || "Unknown").toUpperCase();
};

const stateCode = (value) => String(value || "").toUpperCase();

const subjectLabel = (value) => {
  const found = SUBJECTS.find((s) => s.value === value);
  return found ? found.label : value || "Other";
};

const gameLabel = (value) => {
  const found = GAME_TYPES.find((g) => g.value === value);
  return found ? found.label : value || "None";
};

const gradeRank = (grade) => {
  const index = GRADES.indexOf(grade);
  return index === -1 ? 99 : index;
};

const emptyMultipleChoiceQuestion = () => ({
  question: "",
  questionImage: "",
  answers: [
    { text: "", correct: true, image: "" },
    { text: "", correct: false, image: "" },
    { text: "", correct: false, image: "" },
  ],
});

const emptyDragDropQuestion = () => ({
  question: "",
  options: [
    { id: "item-1", text: "", image: "", targetText: "", targetImage: "" },
    { id: "item-2", text: "", image: "", targetText: "", targetImage: "" },
    { id: "item-3", text: "", image: "", targetText: "", targetImage: "" },
  ],
});

const getStarterQuestionForGameType = (gameType) => {
  const normalized = String(gameType || "").toLowerCase();

  if (normalized === "dragdrop" || normalized === "memorymatch") {
    return emptyDragDropQuestion();
  }

  return emptyMultipleChoiceQuestion();
};

const emptyTopic = () => ({
  _uid: makeUid(),
  _slugLocked: false,
  title: "",
  slug: "",
  standardNumber: "",
  description: "",
  videoUrl: "",
  musicUrl: "",
  gameType: "multipleChoice",
  bonusRoundType: "diamondTap",
  quizId: "",
  imageUrl: "",
  placeholderImage: "",
  visible: true,
  order: 1,
  questions: [emptyMultipleChoiceQuestion()],
});

// Topics that came back from Firestore already have a slug that students'
// saved progress is keyed on, so their slug is locked until you deliberately
// unlock it.
const normalizeLoadedTopic = (topic, index) => ({
  ...emptyTopic(),
  ...topic,
  _uid: makeUid(),
  _slugLocked: Boolean(topic.slug),
  order: topic.order || index + 1,
  questions:
    Array.isArray(topic.questions) && topic.questions.length > 0
      ? topic.questions
      : [getStarterQuestionForGameType(topic.gameType || "multipleChoice")],
});

const getFileNameFromUrl = (url) => {
  if (!url) return "";

  try {
    const decoded = decodeURIComponent(url);
    const parts = decoded.split("/");
    const lastPart = parts[parts.length - 1] || "";
    return lastPart.split("?")[0] || "Uploaded file";
  } catch {
    return "Uploaded file";
  }
};

// Drops the editor-only fields and any half-finished questions before the
// topic is written to Firestore.
const cleanTopicForSave = (topic, index) => {
  const isDragStyle = ["dragdrop", "memorymatch"].includes(
    String(topic.gameType || "").toLowerCase()
  );

  const questions = (Array.isArray(topic.questions) ? topic.questions : [])
    .map((question) => {
      if (isDragStyle) {
        return {
          question: question.question || "",
          options: (question.options || []).filter(
            (option) =>
              option.text ||
              option.image ||
              option.targetText ||
              option.targetImage
          ),
        };
      }

      return {
        question: question.question || "",
        questionImage: question.questionImage || "",
        answers: (question.answers || []).filter(
          (answer) => answer.text || answer.image
        ),
      };
    })
    .filter((question) =>
      isDragStyle
        ? question.question || question.options?.length
        : question.question || question.answers?.length
    );

  const cleaned = {
    ...topic,
    order: index + 1,
    visible: topic.visible !== false,
    questions,
  };

  delete cleaned._uid;
  delete cleaned._slugLocked;

  return cleaned;
};

/* ------------------------------------------------------------------ *
 * Small presentational pieces
 * ------------------------------------------------------------------ */

const Chevron = ({ open }) => (
  <span className={`lb-chevron ${open ? "is-open" : ""}`} aria-hidden="true">
    ▶
  </span>
);

// The V / S / SA / L / G lights beside each topic: Video, Song, Sing-along,
// Lyrics (more than 10 lines), Game.
//
// They answer "what will a student actually get on this page?", the same way
// DynamicPortalPage decides it — including what the Topic Library fills in
// and games stored in the quizzes collection or the built-in quiz library.
// Green = students get it. Amber = it's in the Topic Library but that topic
// is still a Draft. Grey = missing. Hover a light for the reason.
// See src/components/builder/topicMediaStatus.js.
const MediaDots = ({ topic }) => {
  useTopicMediaData(); // re-render when the shared data loads or changes
  const parts = topicMediaStatus(topic);

  return (
    <span className="lb-dots">
      {parts.map((part) => (
        <span
          key={part.key}
          className={`lb-dot lb-dot-${part.key} ${part.state === "on" ? "is-on" : part.state === "draft" ? "is-draft" : ""}`}
          title={part.detail}
          aria-label={part.detail}
        >
          {part.letter}
        </span>
      ))}
    </span>
  );
};

/** Key for the lights, shown once above the topic lists. */
const MediaDotsLegend = () => (
  <div className="lb-dots-legend">
    <span><b className="lb-dot is-on">V</b> Video</span>
    <span><b className="lb-dot is-on">S</b> Song</span>
    <span><b className="lb-dot is-on">SA</b> Sing-along</span>
    <span><b className="lb-dot is-on">L</b> Lyrics (more than 10 lines)</span>
    <span><b className="lb-dot is-on">G</b> Game</span>
    <span className="lb-dots-legend-key">
      <b className="lb-dot is-on">&nbsp;</b> students get it
      <b className="lb-dot is-draft">&nbsp;</b> in the Topic Library, but the topic is a Draft
      <b className="lb-dot">&nbsp;</b> missing
    </span>
    <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => refreshTopicMediaStatus()}>
      ↻ Recheck
    </button>
  </div>
);

/* ------------------------------------------------------------------ *
 * Main component
 * ------------------------------------------------------------------ */

const LayoutBuilder = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    state: "md",
    // Lowercase. layoutService.js lowercases the whole ID on read, so a form
    // defaulting to "K" wrote md_K_science_student while every lookup asked
    // for md_k_science_student — the layout saved fine and was never found.
    grade: "k",
    subject: "science",
    view: "educator",
    title: "Maryland Kindergarten Science",
    theme: "kid",
    tools: { ...DEFAULT_TOOLS },
    topics: [emptyTopic()],
  });

  const [saving, setSaving] = useState(false);

  // MIRROR TO BOTH VIEWS
  // --------------------
  // A page is two independent documents — va_1st_science_educator and
  // va_1st_science_student. The seeders write both. This builder wrote only
  // the one the "Page type" selector pointed at, so a topic added while that
  // said "Educator Page" went live for teachers and stayed invisible to every
  // student, with nothing in the UI to say so.
  //
  // The educator's topic checkboxes cannot rescue that. They live in
  // educatorPortalControls and read as "show unless explicitly false" — they
  // HIDE topics that are in the student layout, they cannot ADD one that
  // isn't. So the teacher sees it ticked on, sees it on their own page, and
  // has no way to tell the student document was never written.
  //
  // Defaults ON because identical views are what you want in nearly every
  // case. Turn it off deliberately when the two pages should genuinely differ.
  const [mirrorViews, setMirrorViews] = useState(true);
  const [allLayouts, setAllLayouts] = useState([]);
  const [loadingLayouts, setLoadingLayouts] = useState(false);

  // Which topic card is expanded. Keyed on _uid, never on slug.
  const [openTopicUid, setOpenTopicUid] = useState(null);

  // Which layout the editor was last filled from, so we can warn when the
  // page selectors have drifted away from it.
  const [loadedLayoutId, setLoadedLayoutId] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [openStates, setOpenStates] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [librarySearch, setLibrarySearch] = useState("");
  const [showAllStates, setShowAllStates] = useState(false);
  const [showHelp, setShowHelp] = useState(true);
  const [toast, setToast] = useState(null);

  const layoutId = useMemo(
    () =>
      makeLayoutId({
        state: form.state,
        grade: form.grade,
        subject: form.subject,
        view: form.view,
      }),
    [form.state, form.grade, form.subject, form.view]
  );

  // The other half of the same page.
  const siblingView = form.view === "educator" ? "student" : "educator";

  const siblingLayoutId = useMemo(
    () =>
      makeLayoutId({
        state: form.state,
        grade: form.grade,
        subject: form.subject,
        view: siblingView,
      }),
    [form.state, form.grade, form.subject, siblingView]
  );

  const previewPath = `/portal/${form.state}/${form.grade}/${form.subject}/${form.view}`;

  const toastTimer = useRef(null);

  const showToast = (message, tone = "ok") => {
    setToast({ message, tone });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  /* ---------------- form field updates ---------------- */

  const updateField = (field, value) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateTool = (toolName) => {
    setDirty(true);
    setForm((prev) => ({
      ...prev,
      tools: { ...prev.tools, [toolName]: !prev.tools[toolName] },
    }));
  };

  const updateTopic = (index, field, value) => {
    setDirty(true);
    setForm((prev) => {
      const topics = [...prev.topics];
      const topic = { ...topics[index], [field]: value };

      // The slug is the student's saved-progress key. Auto-fill it from the
      // title only while the topic is new; once it has been saved, changing
      // it is an explicit choice.
      if (field === "title" && !topic._slugLocked) {
        topic.slug = slugify(value);
      }

      if (field === "slug") {
        topic.slug = slugify(value);
      }

      if (field === "gameType") {
        topic.questions = [getStarterQuestionForGameType(value)];
      }

      topics[index] = topic;
      return { ...prev, topics };
    });
  };

  const unlockSlug = (index) => {
    const ok = window.confirm(
      "Changing the link name will disconnect any stars and scores students " +
        "already earned on this topic.\n\nChange it anyway?"
    );
    if (!ok) return;

    setDirty(true);
    setForm((prev) => {
      const topics = [...prev.topics];
      topics[index] = { ...topics[index], _slugLocked: false };
      return { ...prev, topics };
    });
  };

  const addTopic = () => {
    const fresh = { ...emptyTopic() };

    setDirty(true);
    setForm((prev) => ({
      ...prev,
      topics: [...prev.topics, { ...fresh, order: prev.topics.length + 1 }],
    }));

    // Open it right away so it can never be added invisibly.
    setOpenTopicUid(fresh._uid);

    window.setTimeout(() => {
      const el = document.getElementById(`topic-${fresh._uid}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const removeTopic = (index) => {
    const topic = form.topics[index];
    const name = topic.title || "this untitled topic";

    if (!window.confirm(`Remove ${name} from the editor?`)) return;

    setDirty(true);
    setForm((prev) => ({
      ...prev,
      topics: prev.topics.filter((_, i) => i !== index),
    }));

    if (openTopicUid === topic._uid) setOpenTopicUid(null);
  };

  const moveTopic = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.topics.length) return;

    setDirty(true);
    setForm((prev) => {
      const topics = [...prev.topics];
      [topics[index], topics[target]] = [topics[target], topics[index]];
      return { ...prev, topics: topics.map((t, i) => ({ ...t, order: i + 1 })) };
    });
  };

  /* ---------------- question updates ---------------- */

  const mutateQuestions = (topicIndex, mutator) => {
    setDirty(true);
    setForm((prev) => {
      const topics = [...prev.topics];
      const topic = topics[topicIndex];
      const questions = mutator([...(topic.questions || [])], topic);
      topics[topicIndex] = { ...topic, questions };
      return { ...prev, topics };
    });
  };

  const addQuestion = (topicIndex) =>
    mutateQuestions(topicIndex, (questions, topic) => [
      ...questions,
      getStarterQuestionForGameType(topic.gameType),
    ]);

  const removeQuestion = (topicIndex, questionIndex) =>
    mutateQuestions(topicIndex, (questions) =>
      questions.filter((_, i) => i !== questionIndex)
    );

  const updateQuestionField = (topicIndex, questionIndex, field, value) =>
    mutateQuestions(topicIndex, (questions) => {
      questions[questionIndex] = {
        ...questions[questionIndex],
        [field]: value,
      };
      return questions;
    });

  const updateAnswerField = (
    topicIndex,
    questionIndex,
    answerIndex,
    field,
    value
  ) =>
    mutateQuestions(topicIndex, (questions) => {
      const question = { ...questions[questionIndex] };
      const answers = [...(question.answers || [])];
      answers[answerIndex] = { ...answers[answerIndex], [field]: value };
      question.answers = answers;
      questions[questionIndex] = question;
      return questions;
    });

  const setCorrectAnswer = (topicIndex, questionIndex, answerIndex) =>
    mutateQuestions(topicIndex, (questions) => {
      const question = { ...questions[questionIndex] };
      question.answers = (question.answers || []).map((answer, i) => ({
        ...answer,
        correct: i === answerIndex,
      }));
      questions[questionIndex] = question;
      return questions;
    });

  const addAnswer = (topicIndex, questionIndex) =>
    mutateQuestions(topicIndex, (questions) => {
      const question = { ...questions[questionIndex] };
      question.answers = [
        ...(question.answers || []),
        { text: "", correct: false, image: "" },
      ];
      questions[questionIndex] = question;
      return questions;
    });

  const removeAnswer = (topicIndex, questionIndex, answerIndex) =>
    mutateQuestions(topicIndex, (questions) => {
      const question = { ...questions[questionIndex] };
      const answers = (question.answers || []).filter(
        (_, i) => i !== answerIndex
      );

      if (answers.length > 0 && !answers.some((a) => a.correct)) {
        answers[0] = { ...answers[0], correct: true };
      }

      question.answers = answers;
      questions[questionIndex] = question;
      return questions;
    });

  const updateDragOptionField = (
    topicIndex,
    questionIndex,
    optionIndex,
    field,
    value
  ) =>
    mutateQuestions(topicIndex, (questions) => {
      const question = { ...questions[questionIndex] };
      const options = [...(question.options || [])];
      options[optionIndex] = { ...options[optionIndex], [field]: value };

      if (field === "text") {
        options[optionIndex].id =
          slugify(value) || `item-${optionIndex + 1}`;
      }

      question.options = options;
      questions[questionIndex] = question;
      return questions;
    });

  const addDragOption = (topicIndex, questionIndex) =>
    mutateQuestions(topicIndex, (questions) => {
      const question = { ...questions[questionIndex] };
      const options = [...(question.options || [])];

      options.push({
        id: `item-${options.length + 1}`,
        text: "",
        image: "",
        targetText: "",
        targetImage: "",
      });

      question.options = options;
      questions[questionIndex] = question;
      return questions;
    });

  const removeDragOption = (topicIndex, questionIndex, optionIndex) =>
    mutateQuestions(topicIndex, (questions) => {
      const question = { ...questions[questionIndex] };
      question.options = (question.options || []).filter(
        (_, i) => i !== optionIndex
      );
      questions[questionIndex] = question;
      return questions;
    });

  /* ---------------- loading ---------------- */

  const loadAllLayouts = async () => {
    try {
      setLoadingLayouts(true);
      const layouts = await getAllPortalLayouts();
      setAllLayouts(layouts || []);
    } catch (error) {
      console.error("Error loading portal layouts:", error);
      showToast("Could not load your saved pages. Check the console.", "bad");
    } finally {
      setLoadingLayouts(false);
    }
  };

  useEffect(() => {
    loadAllLayouts();
  }, []);

  useEffect(() => {
    const warn = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const confirmDiscard = () => {
    if (!dirty) return true;
    return window.confirm(
      "You have unsaved changes in the editor. Load this page anyway and lose them?"
    );
  };

  const loadLayoutIntoBuilder = (existingLayout, { silent } = {}) => {
    setForm({
      state: existingLayout.state || "md",
      grade: existingLayout.grade || "k",
      subject: existingLayout.subject || "science",
      view: existingLayout.view || "educator",
      title: existingLayout.title || "",
      theme: existingLayout.theme || "kid",
      tools: { ...DEFAULT_TOOLS, ...(existingLayout.tools || {}) },
      topics: existingLayout.topics?.length
        ? existingLayout.topics.map(normalizeLoadedTopic)
        : [emptyTopic()],
    });

    setLoadedLayoutId(existingLayout.id || null);
    setOpenTopicUid(null);
    setDirty(false);

    if (!silent) {
      showToast(`Opened ${existingLayout.id || "page"} in the editor.`);
    }

    window.setTimeout(() => {
      const el = document.getElementById("lb-editor");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleLoadLayout = async () => {
    if (!confirmDiscard()) return;

    try {
      const existingLayout = await getPortalLayout(layoutId);

      if (!existingLayout) {
        showToast(
          `Nothing saved at ${layoutId} yet. Add topics and save to create it.`,
          "warn"
        );
        return;
      }

      loadLayoutIntoBuilder({ ...existingLayout, id: layoutId });
    } catch (error) {
      console.error("Error loading layout:", error);
      showToast("Could not open that page. Check the console.", "bad");
    }
  };

  /**
   * Throws away everything unsaved and goes back to what is in Firestore.
   *
   * The save bar had one button, and it wrote. There was no way out of a set
   * of edits short of reloading the browser and hoping nothing had been saved
   * along the way — which meant a mistaken drag or a wrong media pick had to be
   * undone by hand, one step at a time.
   *
   * Reloads rather than just clearing: the point is to get back to the saved
   * state, and an empty editor is not that. A page that has never been saved
   * has nothing to go back to, so it resets to a single blank topic instead.
   */
  const handleCancelEdits = async () => {
    if (
      dirty &&
      !window.confirm(
        `Discard unsaved changes to ${layoutId}?\n\n` +
          "The page goes back to what is currently saved. Anything already saved stays."
      )
    ) {
      return;
    }

    try {
      const saved = await getPortalLayout(layoutId);
      if (saved) {
        loadLayoutIntoBuilder({ ...saved, id: layoutId }, { silent: true });
        showToast("Reverted to the saved page.", "ok");
        return;
      }
      // Never saved. Clear back to an empty page rather than leaving edits
      // sitting there with nothing to revert to.
      setForm((f) => ({ ...f, title: "", topics: [emptyTopic()] }));
      setOpenTopicUid(null);
      setDirty(false);
      showToast("Cleared. Nothing was saved at this address yet.", "ok");
    } catch (error) {
      console.error("Error reverting layout:", error);
      showToast("Could not revert. Check the console.", "bad");
    }
  };

  const startNewPage = () => {
    if (!confirmDiscard()) return;

    setForm((prev) => ({
      ...prev,
      title: "",
      tools: { ...DEFAULT_TOOLS },
      topics: [emptyTopic()],
    }));
    setLoadedLayoutId(null);
    setOpenTopicUid(null);
    setDirty(false);
  };

  /* ---------------- library actions ---------------- */

  const openLayoutFromLibrary = (item) => {
    if (!confirmDiscard()) return;
    loadLayoutIntoBuilder(item);
  };

  const openTopicFromLibrary = (item, topicSlug) => {
    if (!confirmDiscard()) return;

    // Build the topic list first so we already know the _uid to open —
    // no second pass, no setState inside a state updater.
    const loadedTopics = (item.topics?.length ? item.topics : [{}]).map(
      normalizeLoadedTopic
    );
    const match = loadedTopics.find((t) => t.slug === topicSlug);

    setForm({
      state: item.state || "md",
      grade: item.grade || "k",
      subject: item.subject || "science",
      view: item.view || "educator",
      title: item.title || "",
      theme: item.theme || "kid",
      tools: { ...DEFAULT_TOOLS, ...(item.tools || {}) },
      topics: loadedTopics,
    });

    setLoadedLayoutId(item.id || null);
    setOpenTopicUid(match ? match._uid : null);
    setDirty(false);

    window.setTimeout(() => {
      const el = match
        ? document.getElementById(`topic-${match._uid}`)
        : document.getElementById("lb-editor");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const addTopicToLayoutFromLibrary = (item) => {
    if (!confirmDiscard()) return;

    const fresh = { ...emptyTopic() };
    const loadedTopics = (item.topics || []).map(normalizeLoadedTopic);

    setForm({
      state: item.state || "md",
      grade: item.grade || "k",
      subject: item.subject || "science",
      view: item.view || "educator",
      title: item.title || "",
      theme: item.theme || "kid",
      tools: { ...DEFAULT_TOOLS, ...(item.tools || {}) },
      topics: [...loadedTopics, { ...fresh, order: loadedTopics.length + 1 }],
    });

    setLoadedLayoutId(item.id || null);
    setOpenTopicUid(fresh._uid);
    setDirty(true);

    window.setTimeout(() => {
      const el = document.getElementById(`topic-${fresh._uid}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const handleDeleteLayout = async (id) => {
    if (
      !window.confirm(
        `Delete this whole page and every topic on it?\n\n${id}\n\nThis cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deletePortalLayout(id);
      showToast("Page deleted.");
      if (loadedLayoutId === id) setLoadedLayoutId(null);
      await loadAllLayouts();
    } catch (error) {
      console.error("Error deleting layout:", error);
      showToast("Could not delete that page. Check the console.", "bad");
    }
  };

  const deleteTopicFromLayout = async (item, topicSlug, topicTitle) => {
    if (
      !window.confirm(
        `Delete "${topicTitle || topicSlug}" from ${item.id}?\n\nThis cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const updatedTopics = (item.topics || [])
        .filter((topic) => topic.slug !== topicSlug)
        .map((topic, index) => ({ ...topic, order: index + 1 }));

      await savePortalLayout(item.id, {
        ...item,
        topics: updatedTopics,
        updatedFrom: "LayoutBuilderTopicDelete",
      });

      // The same asymmetry as saving, in reverse. Deleting from the educator
      // page used to leave the topic on the student page, so a topic pulled
      // because it was wrong stayed live for the people it was wrong for.
      let alsoRemovedFrom = null;

      if (mirrorViews && /_(educator|student)$/.test(item.id)) {
        const twinId = item.id.endsWith("_educator")
          ? item.id.replace(/_educator$/, "_student")
          : item.id.replace(/_student$/, "_educator");

        const twin = allLayouts.find((l) => l.id === twinId);
        const twinHasIt = (twin?.topics || []).some((x) => x.slug === topicSlug);

        if (twin && twinHasIt) {
          await savePortalLayout(twinId, {
            ...twin,
            topics: (twin.topics || [])
              .filter((x) => x.slug !== topicSlug)
              .map((x, index) => ({ ...x, order: index + 1 })),
            updatedFrom: "LayoutBuilderTopicDelete (mirrored)",
          });

          alsoRemovedFrom = twinId;
        }
      }

      showToast(
        alsoRemovedFrom
          ? `Topic deleted from both pages.`
          : "Topic deleted."
      );
      await loadAllLayouts();

      if (loadedLayoutId === item.id) {
        setForm((prev) => ({
          ...prev,
          topics: prev.topics.filter((topic) => topic.slug !== topicSlug),
        }));
      }
    } catch (error) {
      console.error("Error deleting topic:", error);
      showToast("Could not delete that topic. Check the console.", "bad");
    }
  };

  /* ---------------- uploads ---------------- */

  const handleTopicFileUpload = async (index, field, file, mediaType) => {
    if (!file) return;

    const topic = form.topics[index];

    if (!topic.slug) {
      showToast(
        "Give the topic a title first — the title becomes its upload folder.",
        "warn"
      );
      return;
    }

    try {
      showToast("Uploading…", "warn");

      const url = await uploadPortalMedia({
        file,
        layoutId,
        topicSlug: topic.slug,
        mediaType,
      });

      updateTopic(index, field, url);
      showToast("File uploaded. Remember to save the page.");
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Upload failed. Check the console.", "bad");
    }
  };

  const handleQuestionFileUpload = async ({
    topicIndex,
    questionIndex,
    field,
    file,
    mediaType,
  }) => {
    if (!file) return;

    const topic = form.topics[topicIndex];

    if (!topic.slug) {
      showToast("Give the topic a title first.", "warn");
      return;
    }

    try {
      showToast("Uploading…", "warn");

      const url = await uploadPortalMedia({
        file,
        layoutId,
        topicSlug: topic.slug,
        mediaType,
      });

      updateQuestionField(topicIndex, questionIndex, field, url);
      showToast("Image uploaded. Remember to save the page.");
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Upload failed. Check the console.", "bad");
    }
  };

  const handleDragOptionFileUpload = async ({
    topicIndex,
    questionIndex,
    optionIndex,
    field,
    file,
    mediaType,
  }) => {
    if (!file) return;

    const topic = form.topics[topicIndex];

    if (!topic.slug) {
      showToast("Give the topic a title first.", "warn");
      return;
    }

    try {
      showToast("Uploading…", "warn");

      const url = await uploadPortalMedia({
        file,
        layoutId,
        topicSlug: topic.slug,
        mediaType,
      });

      updateDragOptionField(topicIndex, questionIndex, optionIndex, field, url);
      showToast("Image uploaded. Remember to save the page.");
    } catch (error) {
      console.error("Drag/drop image upload error:", error);
      showToast("Upload failed. Check the console.", "bad");
    }
  };

  /* ---------------- saving ---------------- */

  const problems = useMemo(() => {
    const found = [];
    const seen = new Map();

    form.topics.forEach((topic, index) => {
      const label = `Topic ${index + 1}`;

      if (!String(topic.title || "").trim()) {
        found.push({
          uid: topic._uid,
          text: `${label} has no title. Add one or remove the topic.`,
        });
        return;
      }

      if (!topic.slug) {
        found.push({
          uid: topic._uid,
          text: `${label} ("${topic.title}") has no link name.`,
        });
        return;
      }

      if (seen.has(topic.slug)) {
        found.push({
          uid: topic._uid,
          text: `${label} uses the same link name as ${seen.get(
            topic.slug
          )} ("${topic.slug}"). Link names must be unique.`,
        });
      } else {
        seen.set(topic.slug, label);
      }
    });

    if (!String(form.title || "").trim()) {
      found.push({ uid: null, text: "The page needs a title." });
    }

    return found;
  }, [form.topics, form.title]);

  const handleSave = async () => {
    if (problems.length > 0) {
      showToast("Fix the highlighted problems before saving.", "bad");
      const el = document.getElementById("lb-problems");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    try {
      setSaving(true);

      const existingLayout = await getPortalLayout(layoutId);
      const existingCount = existingLayout?.topics?.length || 0;

      // Guard against wiping a page by saving an emptier editor over it. The
      // editor always holds the complete topic list for the page it loaded,
      // so a shrink is only ever intentional.
      if (existingCount > form.topics.length) {
        const ok = window.confirm(
          `${layoutId} currently has ${existingCount} topics and you are about to ` +
            `save ${form.topics.length}.\n\nSave anyway?`
        );
        if (!ok) {
          setSaving(false);
          return;
        }
      }

      const cleanedTopics = form.topics.map(cleanTopicForSave);

      await savePortalLayout(layoutId, {
        ...(existingLayout || {}),
        state: form.state,
        grade: form.grade,
        subject: form.subject,
        view: form.view,
        title: form.title,
        theme: form.theme,
        tools: form.tools,
        topics: cleanedTopics,
        createdFrom: existingLayout?.createdFrom || "LayoutBuilder",
        updatedFrom: "LayoutBuilder",
      });

      // The sibling view. Same topics, same tools — only `view` differs, and
      // `title` is left as whatever that document already had so a page with
      // a deliberately different heading keeps it.
      //
      // Its own existing doc is spread in first for the same reason the
      // primary save does it: a field the builder has never heard of (added by
      // a seeder, or by hand in the console) survives the write instead of
      // being dropped.
      let mirrored = 0;

      if (mirrorViews) {
        const existingSibling = await getPortalLayout(siblingLayoutId);
        const siblingCount = existingSibling?.topics?.length || 0;

        // Same shrink guard as above, asked separately. The sibling can hold a
        // longer list than the editor if it drifted, and silently truncating
        // the student page while "saving the educator page" is exactly the
        // class of surprise this whole change exists to remove.
        let proceed = true;

        if (siblingCount > cleanedTopics.length) {
          proceed = window.confirm(
            `${siblingLayoutId} currently has ${siblingCount} topics and mirroring ` +
              `would leave it with ${cleanedTopics.length}.\n\n` +
              "Mirror anyway? Cancel keeps that page as it is and saves only " +
              `${layoutId}.`
          );
        }

        if (proceed) {
          await savePortalLayout(siblingLayoutId, {
            ...(existingSibling || {}),
            state: form.state,
            grade: form.grade,
            subject: form.subject,
            view: siblingView,
            title: existingSibling?.title || form.title,
            theme: form.theme,
            tools: form.tools,
            topics: cleanedTopics,
            createdFrom: existingSibling?.createdFrom || "LayoutBuilder",
            updatedFrom: "LayoutBuilder (mirrored)",
          });

          mirrored = cleanedTopics.length;
        }
      }

      // Re-lock every slug now that students can hit them.
      setForm((prev) => ({
        ...prev,
        topics: prev.topics.map((topic, index) => ({
          ...topic,
          order: index + 1,
          _slugLocked: Boolean(topic.slug),
        })),
      }));

      setLoadedLayoutId(layoutId);
      setDirty(false);
      showToast(
        mirrored
          ? `Saved ${cleanedTopics.length} topics to both the educator and student pages.`
          : `Saved ${layoutId} — ${cleanedTopics.length} topics live. ` +
            `The ${siblingView} page was not changed.`
      );
      await loadAllLayouts();
    } catch (error) {
      console.error("Error saving layout:", error);
      showToast("Save failed. Check the console.", "bad");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- library grouping ---------------- */

  const groupedLibrary = useMemo(() => {
    const term = librarySearch.trim().toLowerCase();

    const matches = (item) => {
      if (!term) return true;
      const haystack = [
        item.id,
        item.title,
        stateLabel(item.state),
        item.grade,
        subjectLabel(item.subject),
        ...(item.topics || []).map((t) => t.title),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    };

    const states = new Map();

    // Every state the signup form offers gets a shelf, whether or not
    // anything has been built for it. An empty shelf is the point: it's the
    // only place in the app that shows a state you're selling and haven't
    // built. Turn it off with the toggle when you just want your own work.
    if (showAllStates && !term) {
      STATES.forEach((state) => {
        if (!states.has(state.value)) states.set(state.value, new Map());
      });
    }

    allLayouts.filter(matches).forEach((item) => {
      const stateKey = item.state || "unknown";
      if (!states.has(stateKey)) states.set(stateKey, new Map());

      const groupKey = `${item.grade || "?"}|${item.subject || "?"}`;
      const groups = states.get(stateKey);
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(item);
    });

    return [...states.entries()]
      .map(([stateKey, groups]) => {
        const groupList = [...groups.entries()]
          .map(([groupKey, layouts]) => {
            const [grade, subject] = groupKey.split("|");
            return {
              key: `${stateKey}|${groupKey}`,
              grade,
              subject,
              layouts: [...layouts].sort((a, b) =>
                String(a.view).localeCompare(String(b.view))
              ),
              topicCount: layouts.reduce(
                (sum, l) => sum + (l.topics?.length || 0),
                0
              ),
            };
          })
          .sort(
            (a, b) =>
              gradeRank(a.grade) - gradeRank(b.grade) ||
              a.subject.localeCompare(b.subject)
          );

        // Which of the four subjects actually have material in this state,
        // and how much. A subject with pages but zero topics is an empty
        // shell, so it's counted separately rather than shown as covered —
        // that distinction is what tells you a state is only half-seeded.
        const subjectTotals = SUBJECTS.map((subject) => {
          const forSubject = groupList.filter((g) => g.subject === subject.value);
          const topics = forSubject.reduce((sum, g) => sum + g.topicCount, 0);
          return {
            value: subject.value,
            label: subject.label,
            pages: forSubject.reduce((sum, g) => sum + g.layouts.length, 0),
            topics,
            grades: [...new Set(forSubject.map((g) => g.grade))].length,
            has: topics > 0,
          };
        });

        return {
          key: stateKey,
          label: stateLabel(stateKey),
          code: stateCode(stateKey),
          availability: stateAvailability(stateKey),
          subjectTotals,
          groups: groupList,
          pageCount: groupList.reduce((sum, g) => sum + g.layouts.length, 0),
          topicCount: groupList.reduce((sum, g) => sum + g.topicCount, 0),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allLayouts, librarySearch, showAllStates]);

  const toggleState = (key) =>
    setOpenStates((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleGroup = (key) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const expandAllLibrary = () => {
    const states = {};
    const groups = {};
    groupedLibrary.forEach((state) => {
      states[state.key] = true;
      state.groups.forEach((group) => {
        groups[group.key] = true;
      });
    });
    setOpenStates(states);
    setOpenGroups(groups);
  };

  const collapseAllLibrary = () => {
    setOpenStates({});
    setOpenGroups({});
  };

  // "Start a page here" on a state shelf. Points the editor at that state
  // (and, when you clicked through from a subject chip, that subject too),
  // leaves grade and view alone, and scrolls up. It deliberately does NOT
  // clear topics you have unsaved — the drift banner already warns when the
  // selectors have moved away from what was loaded.
  const startPageForState = (stateKey, subject) => {
    setDirty(true);
    setForm((prev) => ({
      ...prev,
      state: stateKey,
      subject: subject || prev.subject,
    }));
    document
      .getElementById("lb-editor")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    showToast(
      `Builder pointed at ${stateLabel(stateKey)}${
        subject ? ` · ${subjectLabel(subject)}` : ""
      }.`
    );
  };

  const drifted = loadedLayoutId && loadedLayoutId !== layoutId;

  /* ---------------- question editor ---------------- */

  const renderQuestionEditor = (topic, topicIndex) => {
    const gameType = String(topic.gameType || "").toLowerCase();
    const isDragStyle = gameType === "dragdrop" || gameType === "memorymatch";
    const questions = Array.isArray(topic.questions) ? topic.questions : [];

    return (
      <div className="lb-questions">
        <div className="lb-sub-head">
          <div>
            <h4>Questions for the {gameLabel(topic.gameType)} game</h4>
            <p className="lb-hint">
              {isDragStyle
                ? "Each match has an item and the target it belongs with."
                : "Mark exactly one answer as correct on every question."}
            </p>
          </div>
          <button
            type="button"
            className="lb-btn lb-btn-soft"
            onClick={() => addQuestion(topicIndex)}
          >
            + Add question
          </button>
        </div>

        {questions.length === 0 && (
          <p className="lb-empty">
            No questions yet. Add one so this topic has a game.
          </p>
        )}

        {questions.map((question, questionIndex) => (
          <div className="lb-question" key={questionIndex}>
            <div className="lb-question-head">
              <span className="lb-num">{questionIndex + 1}</span>
              <button
                type="button"
                className="lb-btn lb-btn-danger lb-btn-sm"
                onClick={() => removeQuestion(topicIndex, questionIndex)}
                disabled={questions.length === 1}
              >
                Remove
              </button>
            </div>

            <label className="lb-field lb-field-wide">
              <span className="lb-label">Question</span>
              <textarea
                value={question.question || ""}
                onChange={(e) =>
                  updateQuestionField(
                    topicIndex,
                    questionIndex,
                    "question",
                    e.target.value
                  )
                }
                placeholder={
                  isDragStyle
                    ? "Match each plant part to its job."
                    : "What do plants need to grow?"
                }
              />
            </label>

            {!isDragStyle && (
              <>
                <div className="lb-grid">
                  <label className="lb-field">
                    <span className="lb-label">Question image URL</span>
                    <input
                      value={question.questionImage || ""}
                      onChange={(e) =>
                        updateQuestionField(
                          topicIndex,
                          questionIndex,
                          "questionImage",
                          e.target.value
                        )
                      }
                      placeholder="Optional"
                    />
                  </label>

                  <label className="lb-field">
                    <span className="lb-label">Or upload an image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleQuestionFileUpload({
                          topicIndex,
                          questionIndex,
                          field: "questionImage",
                          file: e.target.files[0],
                          mediaType: "question-images",
                        })
                      }
                    />
                  </label>
                </div>

                <div className="lb-answers">
                  <div className="lb-sub-head">
                    <h5>Answers</h5>
                    <button
                      type="button"
                      className="lb-btn lb-btn-soft lb-btn-sm"
                      onClick={() => addAnswer(topicIndex, questionIndex)}
                    >
                      + Add answer
                    </button>
                  </div>

                  {(question.answers || []).map((answer, answerIndex) => (
                    <div
                      className={`lb-answer ${
                        answer.correct ? "is-correct" : ""
                      }`}
                      key={answerIndex}
                    >
                      <label className="lb-radio">
                        <input
                          type="radio"
                          name={`correct-${topic._uid}-${questionIndex}`}
                          checked={answer.correct === true}
                          onChange={() =>
                            setCorrectAnswer(
                              topicIndex,
                              questionIndex,
                              answerIndex
                            )
                          }
                        />
                        <span>Correct</span>
                      </label>

                      <input
                        className="lb-answer-text"
                        value={answer.text || ""}
                        onChange={(e) =>
                          updateAnswerField(
                            topicIndex,
                            questionIndex,
                            answerIndex,
                            "text",
                            e.target.value
                          )
                        }
                        placeholder={`Answer ${answerIndex + 1}`}
                      />

                      <input
                        className="lb-answer-image"
                        value={answer.image || ""}
                        onChange={(e) =>
                          updateAnswerField(
                            topicIndex,
                            questionIndex,
                            answerIndex,
                            "image",
                            e.target.value
                          )
                        }
                        placeholder="Image URL (optional)"
                      />

                      <button
                        type="button"
                        className="lb-btn lb-btn-danger lb-btn-sm"
                        onClick={() =>
                          removeAnswer(topicIndex, questionIndex, answerIndex)
                        }
                        disabled={(question.answers || []).length <= 2}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {isDragStyle && (
              <div className="lb-answers">
                <div className="lb-sub-head">
                  <h5>
                    {gameType === "memorymatch"
                      ? "Memory match pairs"
                      : "Drag and drop matches"}
                  </h5>
                  <button
                    type="button"
                    className="lb-btn lb-btn-soft lb-btn-sm"
                    onClick={() => addDragOption(topicIndex, questionIndex)}
                  >
                    + Add match
                  </button>
                </div>

                {(question.options || []).map((option, optionIndex) => (
                  <div className="lb-match" key={optionIndex}>
                    <div className="lb-match-side">
                      <span className="lb-match-tag">Item</span>

                      <input
                        value={option.text || ""}
                        onChange={(e) =>
                          updateDragOptionField(
                            topicIndex,
                            questionIndex,
                            optionIndex,
                            "text",
                            e.target.value
                          )
                        }
                        placeholder="Frog"
                      />

                      <input
                        value={option.image || ""}
                        onChange={(e) =>
                          updateDragOptionField(
                            topicIndex,
                            questionIndex,
                            optionIndex,
                            "image",
                            e.target.value
                          )
                        }
                        placeholder="Image URL (optional)"
                      />

                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleDragOptionFileUpload({
                            topicIndex,
                            questionIndex,
                            optionIndex,
                            field: "image",
                            file: e.target.files[0],
                            mediaType: "memory-match-item-images",
                          })
                        }
                      />
                    </div>

                    <span className="lb-match-arrow" aria-hidden="true">
                      →
                    </span>

                    <div className="lb-match-side">
                      <span className="lb-match-tag">Goes with</span>

                      <input
                        value={option.targetText || ""}
                        onChange={(e) =>
                          updateDragOptionField(
                            topicIndex,
                            questionIndex,
                            optionIndex,
                            "targetText",
                            e.target.value
                          )
                        }
                        placeholder="Amphibian"
                      />

                      <input
                        value={option.targetImage || ""}
                        onChange={(e) =>
                          updateDragOptionField(
                            topicIndex,
                            questionIndex,
                            optionIndex,
                            "targetImage",
                            e.target.value
                          )
                        }
                        placeholder="Image URL (optional)"
                      />

                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleDragOptionFileUpload({
                            topicIndex,
                            questionIndex,
                            optionIndex,
                            field: "targetImage",
                            file: e.target.files[0],
                            mediaType: "memory-match-target-images",
                          })
                        }
                      />
                    </div>

                    <button
                      type="button"
                      className="lb-btn lb-btn-danger lb-btn-sm"
                      onClick={() =>
                        removeDragOption(topicIndex, questionIndex, optionIndex)
                      }
                      disabled={(question.options || []).length <= 2}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  /* ---------------- topic card ---------------- */

  const renderTopicCard = (topic, index) => {
    const isOpen = openTopicUid === topic._uid;
    const hasProblem = problems.some((p) => p.uid === topic._uid);

    return (
      <div
        className={`lb-topic ${isOpen ? "is-open" : ""} ${
          hasProblem ? "has-problem" : ""
        }`}
        id={`topic-${topic._uid}`}
        key={topic._uid}
      >
        <div className="lb-topic-head">
          <button
            type="button"
            className="lb-topic-toggle"
            onClick={() => setOpenTopicUid(isOpen ? null : topic._uid)}
            aria-expanded={isOpen}
          >
            <Chevron open={isOpen} />
            <span className="lb-topic-order">{index + 1}</span>
            <span className="lb-topic-name">
              {topic.title || <em>Untitled topic — click to fill it in</em>}
            </span>
            {topic.standardNumber && (
              <span className="lb-pill">{topic.standardNumber}</span>
            )}
            {topic.visible === false && (
              <span className="lb-pill lb-pill-muted">Hidden</span>
            )}
          </button>

          <div className="lb-topic-actions">
            <MediaDots topic={topic} />
            <button
              type="button"
              className="lb-icon-btn"
              onClick={() => moveTopic(index, -1)}
              disabled={index === 0}
              title="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              className="lb-icon-btn"
              onClick={() => moveTopic(index, 1)}
              disabled={index === form.topics.length - 1}
              title="Move down"
            >
              ↓
            </button>
            <button
              type="button"
              className="lb-btn lb-btn-danger lb-btn-sm"
              onClick={() => removeTopic(index)}
            >
              Remove
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="lb-topic-body">
            <div className="lb-grid">
              <label className="lb-field">
                <span className="lb-label">Topic title</span>
                <input
                  value={topic.title}
                  onChange={(e) => updateTopic(index, "title", e.target.value)}
                  placeholder="Animal habitats"
                />
                <span className="lb-hint">
                  This is what kids see on the topic card.
                </span>
              </label>

              <label className="lb-field">
                <span className="lb-label">Standard number</span>
                <input
                  value={topic.standardNumber}
                  onChange={(e) =>
                    updateTopic(index, "standardNumber", e.target.value)
                  }
                  placeholder="LS.K.1"
                />
                <span className="lb-hint">
                  Optional. Shows as a badge on the topic card.
                </span>
              </label>
            </div>

            <div className="lb-slug-row">
              <div className="lb-slug-value">
                <span className="lb-label">Link name</span>
                <code>{topic.slug || "—"}</code>
              </div>

              {topic._slugLocked ? (
                <div className="lb-slug-note">
                  <span>
                    Locked. Student stars and scores are saved under this name.
                  </span>
                  <button
                    type="button"
                    className="lb-btn lb-btn-soft lb-btn-sm"
                    onClick={() => unlockSlug(index)}
                  >
                    Change it
                  </button>
                </div>
              ) : (
                <input
                  className="lb-slug-input"
                  value={topic.slug}
                  onChange={(e) => updateTopic(index, "slug", e.target.value)}
                  placeholder="animal-habitats"
                />
              )}
            </div>

            <label className="lb-field lb-field-wide">
              <span className="lb-label">Description</span>
              <textarea
                value={topic.description}
                onChange={(e) =>
                  updateTopic(index, "description", e.target.value)
                }
                placeholder="One or two sentences a first grader can read."
              />
            </label>

            <div className="lb-media">
              <h4>Song and video</h4>
              <p className="lb-hint">
                Paste a URL, pick one you already uploaded, or upload a new
                file. Uploads land in a folder named after the link name above.
              </p>

              <div className="lb-media-row">
                <label className="lb-field">
                  <span className="lb-label">Video URL</span>
                  <input
                    value={topic.videoUrl}
                    onChange={(e) =>
                      updateTopic(index, "videoUrl", e.target.value)
                    }
                    placeholder="Firebase Storage or YouTube URL"
                  />
                  {topic.videoUrl && (
                    <span className="lb-file">
                      {getFileNameFromUrl(topic.videoUrl)}
                    </span>
                  )}
                </label>

                <div className="lb-field">
                  <span className="lb-label">Pick or upload</span>
                  <MediaPicker
                    type="video"
                    topicSlug={topic.slug}
                    onSelect={(url) => updateTopic(index, "videoUrl", url)}
                  />
                  <input
                    type="file"
                    accept="video/*"
                    disabled={!topic.slug}
                    onChange={(e) =>
                      handleTopicFileUpload(
                        index,
                        "videoUrl",
                        e.target.files[0],
                        "videos"
                      )
                    }
                  />
                </div>
              </div>

              <div className="lb-media-row">
                <label className="lb-field">
                  <span className="lb-label">Song URL</span>
                  <input
                    value={topic.musicUrl}
                    onChange={(e) =>
                      updateTopic(index, "musicUrl", e.target.value)
                    }
                    placeholder="Firebase Storage audio URL"
                  />
                  {topic.musicUrl && (
                    <span className="lb-file">
                      {getFileNameFromUrl(topic.musicUrl)}
                    </span>
                  )}
                </label>

                <div className="lb-field">
                  <span className="lb-label">Pick or upload</span>
                  <MediaPicker
                    type="music"
                    topicSlug={topic.slug}
                    onSelect={(url) => updateTopic(index, "musicUrl", url)}
                  />
                  <input
                    type="file"
                    accept="audio/*"
                    disabled={!topic.slug}
                    onChange={(e) =>
                      handleTopicFileUpload(
                        index,
                        "musicUrl",
                        e.target.files[0],
                        "music"
                      )
                    }
                  />
                </div>
              </div>

              {!topic.slug && (
                <p className="lb-warn-inline">
                  Add a title before uploading — the uploader needs a folder
                  name.
                </p>
              )}
            </div>

            <div className="lb-media">
              <h4>Pictures</h4>

              <div className="lb-media-row">
                <label className="lb-field">
                  <span className="lb-label">Topic card image URL</span>
                  <input
                    value={topic.imageUrl}
                    onChange={(e) =>
                      updateTopic(index, "imageUrl", e.target.value)
                    }
                    placeholder="Optional"
                  />
                </label>

                <label className="lb-field">
                  <span className="lb-label">Upload topic card image</span>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={!topic.slug}
                    onChange={(e) =>
                      handleTopicFileUpload(
                        index,
                        "imageUrl",
                        e.target.files[0],
                        "images"
                      )
                    }
                  />
                </label>
              </div>

              <div className="lb-media-row">
                <label className="lb-field">
                  <span className="lb-label">Game background URL</span>
                  <input
                    value={topic.placeholderImage || ""}
                    onChange={(e) =>
                      updateTopic(index, "placeholderImage", e.target.value)
                    }
                    placeholder="Optional"
                  />
                </label>

                <label className="lb-field">
                  <span className="lb-label">Upload game background</span>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={!topic.slug}
                    onChange={(e) =>
                      handleTopicFileUpload(
                        index,
                        "placeholderImage",
                        e.target.files[0],
                        "game-backgrounds"
                      )
                    }
                  />
                </label>
              </div>
            </div>

            <div className="lb-media">
              <h4>Game</h4>

              <div className="lb-grid">
                <label className="lb-field">
                  <span className="lb-label">Game type</span>
                  <select
                    value={topic.gameType}
                    onChange={(e) =>
                      updateTopic(index, "gameType", e.target.value)
                    }
                  >
                    {GAME_TYPES.map((game) => (
                      <option key={game.value} value={game.value}>
                        {game.label}
                      </option>
                    ))}
                  </select>
                  <span className="lb-hint">
                    Switching this resets the questions below.
                  </span>
                </label>

                <label className="lb-field">
                  <span className="lb-label">Bonus round</span>
                  <select
                    value={topic.bonusRoundType || "diamondTap"}
                    onChange={(e) =>
                      updateTopic(index, "bonusRoundType", e.target.value)
                    }
                  >
                    {BONUS_ROUNDS.map((bonus) => (
                      <option key={bonus.value} value={bonus.value}>
                        {bonus.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="lb-field">
                  <span className="lb-label">Quiz ID</span>
                  <input
                    value={topic.quizId}
                    onChange={(e) =>
                      updateTopic(index, "quizId", e.target.value)
                    }
                    placeholder="Optional existing quiz document id"
                  />
                </label>

                <label className="lb-checkbox">
                  <input
                    type="checkbox"
                    checked={topic.visible !== false}
                    onChange={(e) =>
                      updateTopic(index, "visible", e.target.checked)
                    }
                  />
                  <span>Show this topic to students</span>
                </label>
              </div>
            </div>

            {renderQuestionEditor(topic, index)}
          </div>
        )}
      </div>
    );
  };

  /* ---------------- render ---------------- */

  return (
    <div className="layout-builder-page lb-page">
      {toast && <div className={`lb-toast lb-toast-${toast.tone}`}>{toast.message}</div>}

      <header className="lb-header">
        <div>
          <p className="lb-eyebrow">Smart Board Learning</p>
          <h1>Page Builder</h1>
          <p className="lb-sub">
            Build a student or educator page for any state, grade and subject —
            no code.
          </p>
        </div>

        <div className="lb-header-actions">
          <button
            type="button"
            className="lb-btn lb-btn-ghost"
            onClick={() => navigate("/admin")}
          >
            Back to admin
          </button>
          <button
            type="button"
            className="lb-btn lb-btn-ghost"
            onClick={() => navigate(previewPath)}
          >
            Preview page
          </button>
        </div>
      </header>

      <section className="lb-card lb-help">
        <button
          type="button"
          className="lb-help-toggle"
          onClick={() => setShowHelp((v) => !v)}
          aria-expanded={showHelp}
        >
          <Chevron open={showHelp} />
          How this works
        </button>

        {showHelp && (
          <ol className="lb-steps">
            <li>
              <strong>Pick the page.</strong> Choose state, grade, subject and
              whether it's the student or educator page. That combination is the
              page's address.
            </li>
            <li>
              <strong>Open it.</strong> Hit <em>Open this page</em> to pull in
              what's already saved, or start fresh to make a new one.
            </li>
            <li>
              <strong>Choose the tools.</strong> Turn on the buttons kids should
              see: song, video, quiz, game, slideshow.
            </li>
            <li>
              <strong>Add topics.</strong> Each topic is one card on the page —
              a song, a video, and a game. Click a topic to open it.
            </li>
            <li>
              <strong>Save.</strong> Nothing is live until you hit save. Your
              whole library is at the bottom of this screen, folded up by state.
            </li>
          </ol>
        )}
      </section>

      {/* ---------------- Step 1 ---------------- */}
      <section className="lb-card" id="lb-editor">
        <div className="lb-card-head">
          <span className="lb-step">1</span>
          <div>
            <h2>Which page are you building?</h2>
            <p className="lb-hint">
              Changing any of these four points the builder at a different page.
            </p>
          </div>
        </div>

        <div className="lb-grid lb-grid-4">
          <label className="lb-field">
            <span className="lb-label">State</span>
            <select
              value={form.state}
              onChange={(e) => updateField("state", e.target.value)}
            >
              {STATE_SELECT_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          <label className="lb-field">
            <span className="lb-label">Grade</span>
            <select
              value={form.grade}
              onChange={(e) => updateField("grade", e.target.value)}
            >
              {GRADES.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </label>

          <label className="lb-field">
            <span className="lb-label">Subject</span>
            <select
              value={form.subject}
              onChange={(e) => updateField("subject", e.target.value)}
            >
              {SUBJECTS.map((subject) => (
                <option key={subject.value} value={subject.value}>
                  {subject.label}
                </option>
              ))}
            </select>
          </label>

          <label className="lb-field">
            <span className="lb-label">Page type</span>
            <select
              value={form.view}
              onChange={(e) => updateField("view", e.target.value)}
            >
              {VIEWS.map((view) => (
                <option key={view.value} value={view.value}>
                  {view.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="lb-field lb-field-wide lb-mirror">
          <input
            type="checkbox"
            checked={mirrorViews}
            onChange={(e) => setMirrorViews(e.target.checked)}
          />
          <span>
            <strong>Save to both the educator and student pages</strong>
            <em>
              {mirrorViews
                ? `Topics go to ${form.view === "educator" ? "students" : "teachers"} too. Turn this off only if the two pages should differ.`
                : `Only the ${form.view} page will change. The ${siblingView} page keeps whatever it has now — students will not see anything you add here.`}
            </em>
          </span>
        </label>

        <label className="lb-field lb-field-wide">
          <span className="lb-label">Page title</span>
          <input
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            placeholder="Maryland Kindergarten Science"
          />
        </label>

        <div className="lb-address">
          <div>
            <span className="lb-label">Saves to</span>
            <code>{layoutId}</code>
            {mirrorViews && (
              <>
                {" and "}
                <code>{siblingLayoutId}</code>
              </>
            )}
          </div>

          <div className="lb-address-actions">
            <button
              type="button"
              className="lb-btn lb-btn-soft"
              onClick={handleLoadLayout}
            >
              Open this page
            </button>
            <button
              type="button"
              className="lb-btn lb-btn-ghost"
              onClick={startNewPage}
            >
              Start fresh
            </button>
          </div>
        </div>

        {drifted && (
          <p className="lb-warn">
            You opened <code>{loadedLayoutId}</code> but the selectors now point
            at <code>{layoutId}</code>. Saving will write these topics to{" "}
            <code>{layoutId}</code>.
          </p>
        )}
      </section>

      {/* ---------------- Step 2 ---------------- */}
      <section className="lb-card">
        <div className="lb-card-head">
          <span className="lb-step">2</span>
          <div>
            <h2>Which buttons show up?</h2>
            <p className="lb-hint">
              Tap to turn each one on or off for every topic on this page.
            </p>
          </div>
        </div>

        <div className="lb-tools">
          {Object.keys(form.tools).map((toolName) => (
            <button
              type="button"
              key={toolName}
              className={`lb-tool ${form.tools[toolName] ? "is-on" : ""}`}
              onClick={() => updateTool(toolName)}
              aria-pressed={form.tools[toolName]}
            >
              <span className="lb-tool-check">
                {form.tools[toolName] ? "✓" : ""}
              </span>
              {TOOL_LABELS[toolName] || toolName}
            </button>
          ))}
        </div>
      </section>

      {/* ---------------- Step 3 ---------------- */}
      <section className="lb-card">
        <div className="lb-card-head">
          <span className="lb-step">3</span>
          <div>
            <h2>Topics on this page</h2>
            <p className="lb-hint">
              {form.topics.length} topic
              {form.topics.length === 1 ? "" : "s"} · click one to open it ·
              arrows change the order kids see
            </p>
          </div>

          <button type="button" className="lb-btn" onClick={addTopic}>
            + Add topic
          </button>
        </div>

        {problems.length > 0 && (
          <div className="lb-problems" id="lb-problems">
            <strong>Fix these before saving</strong>
            <ul>
              {problems.map((problem, i) => (
                <li key={i}>{problem.text}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="lb-topics">
          {form.topics.length === 0 && (
            <p className="lb-empty">
              No topics yet. Add the first one to start this page.
            </p>
          )}
          {form.topics.map(renderTopicCard)}
        </div>

        <div className="lb-topics-foot">
          <button type="button" className="lb-btn" onClick={addTopic}>
            + Add topic
          </button>
        </div>
      </section>

      {/* ---------------- State availability ---------------- */}
      {/* Which states and subjects appear at signup. Reads and writes
          stateSettings ONLY — never portalLayouts, users or subscriptions, so
          nothing here can change a saved page or somebody's account.

          Switching a subject off closes it to NEW signups. Everyone already
          using it keeps working: losing access to something you paid for
          because of an internal content decision is not a thing the person
          can fix themselves. */}
      <StateAvailabilityPanel />

      {/* ---------------- Suggested topics ---------------- */}
      {/* The content backlog: standards with no topic, ordered by how many
          states need them. Reads contentGaps; writes a slug back to it, and
          on "Mark complete" writes the topic onto every state page that needs
          that standard — each labelled with its own code. */}
      <SuggestedTopicsPanel />

      {/* ---------------- Topic library ---------------- */}
      {/* Canonical topics, shared across every state that uses the slug.
          Reads and writes topicLibrary and mediaLibrary only — it never
          touches portalLayouts, so nothing here can change a saved page. */}
      <TopicLibraryPanel />

      {/* ---------------- Topic pictures ---------------- */}
      {/* AI cartoons for the topic cards students and educators see. New
          pictures wait in topicLibrary.imageDraft until approved; approving
          sets topicLibrary.imageUrl (a small WebP in topic-images/cards/).
          Touches portalLayouts only through its "Let the library picture
          show" button, which backs each page up to layoutBackups first. */}
      <TopicPicturesPanel />

      {/* ---------------- Game generator ---------------- */}
      {/* Makes games from each topic's lyrics. Drafts live in generatedGames;
          only "Publish" writes the live game to quizzes/{slug}, backing up
          whatever was there to quizBackups first. */}
      <GameGeneratorPanel />

      {/* ---------------- Sing-along studio ---------------- */}
      {/* Times each song's lyrics so they light up as they're sung, publishes
          the sing-along to the portal's Music button, and records MP4s.
          Reads topicLibrary; writes only lyricSync and, for a saved video,
          videos/{slug}{year}singalong.mp4. Never touches portalLayouts or a
          topic's own media, so nothing here can change a saved page. */}
      <SingAlongPanel />

      {/* ---------------- Lesson video studio ---------------- */}
      {/* Animated lesson videos (students + teaching scene + lyrics) built
          from a storyboard and the song's sing-along timings. Reads
          topicLibrary and lyricSync; writes only when asked: the MP4 at
          videos/{slug}{year}animated.mp4, lessonVideo* fields on lyricSync,
          and topicLibrary.videoUrl via "Use the animated video". */}
      <LessonVideoPanel />

      {/* ---------------- Video upload studio ---------------- */}
      {/* Your OWN uploaded video, with the song's lyrics along the bottom
          (word by word, from lyricSync) and pictures shown between times you
          choose. Separate from the Lesson video studio: its own collection
          (videoProjects), renderer and exporter. Writes only when asked: the
          source upload under video-studio/, the MP4 at
          videos/{slug}{year}myvideo.mp4, and topicLibrary.videoUrl /
          singAlongUrl through its two "Use as …" buttons. */}
      <VideoUploadPanel />

      {/* ---------------- Game library ---------------- */}
      {/* Every game and every bonus round, in any combination, on generic
          sample content. Reads and writes nothing at all — no Firestore, no
          scores, no progress, no leaderboard — so a run here cannot touch a
          student's record or a saved page. Collapsed by default: the game
          components are dynamically imported, and a component stylesheet in
          CRA is global from the moment it enters the bundle, so keeping them
          out until someone presses Play keeps eleven game stylesheets off
          this page for everyone who never opens it. */}
      <GameLibraryPanel />

      {/* ---------------- Shop items ---------------- */}
      {/* The student shop's catalogue (shopItems). Prices set here are what
          the buyShopItem Cloud Function charges. New items can get an AI
          picture with its white background cut out. */}
      {/* Shop items, avatar-maker prices and coin amounts — all in one place */}
      <RewardsSettingsPanel />
      {/* Voice, tone and speed of every read-aloud in the app */}
      <VoiceSettingsPanel />
      {/* Uploaded sounds for right / wrong / good job / bonus-round moments */}
      <GameSoundsPanel />
      <TreasureMapPagesPanel />
      <SeasonDatesPanel />

      {/* ---------------- Home page demo ---------------- */}
      {/* The free "try it" class on the public home page: publishes the
          sample topics to publicDemo/home (the only thing it writes) and
          shows the visitor / click / game-play tracker (demoStats). */}
      <HomeDemoPanel />

      {/* ---------------- Library ---------------- */}
      <section className="lb-card lb-library">
        <div className="lb-card-head">
          <span className="lb-step lb-step-alt">★</span>
          <div>
            <h2>Your library</h2>
            <p className="lb-hint">
              Every page you've saved, folded up by state, then grade and
              subject. Open a folder to see its topics.
            </p>
          </div>

          <div className="lb-library-tools">
            <input
              className="lb-search"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              placeholder="Search pages and topics"
            />
            <button
              type="button"
              className="lb-btn lb-btn-ghost lb-btn-sm"
              onClick={expandAllLibrary}
            >
              Expand all
            </button>
            <button
              type="button"
              className="lb-btn lb-btn-ghost lb-btn-sm"
              onClick={collapseAllLibrary}
            >
              Collapse all
            </button>
            <button
              type="button"
              className="lb-btn lb-btn-soft lb-btn-sm"
              onClick={loadAllLayouts}
            >
              {loadingLayouts ? "Refreshing…" : "Refresh"}
            </button>
            <label className="lb-toggle-inline">
              <input
                type="checkbox"
                checked={showAllStates}
                onChange={(e) => setShowAllStates(e.target.checked)}
              />
              Show every state offered at signup
            </label>
          </div>
        </div>

        <MediaDotsLegend />

        {groupedLibrary.length === 0 && (
          <p className="lb-empty">
            {librarySearch
              ? "Nothing matches that search."
              : "No pages saved yet. Build one above and save it."}
          </p>
        )}

        {groupedLibrary.map((state) => {
          const stateOpen = Boolean(openStates[state.key]);

          return (
            <div className="lb-acc" key={state.key}>
              <div className="lb-acc-headrow">
                <button
                  type="button"
                  className="lb-acc-head"
                  onClick={() => toggleState(state.key)}
                  aria-expanded={stateOpen}
                >
                  <Chevron open={stateOpen} />
                  <span className="lb-acc-title">{state.label}</span>
                  <span className="lb-code" title="Code used in layout IDs, URLs and role strings">
                    {state.code}
                  </span>
                  <span
                    className={`lb-avail lb-avail-${state.availability.key}`}
                    title="What the signup and free-trial dropdown promises for this state"
                  >
                    {state.availability.label}
                  </span>
                  <span className="lb-count">{state.pageCount} pages</span>
                  <span className="lb-count lb-count-alt">
                    {state.topicCount} topics
                  </span>
                </button>

                <button
                  type="button"
                  className="lb-btn lb-btn-soft lb-btn-sm lb-acc-start"
                  onClick={() => startPageForState(state.key)}
                >
                  Start a page here
                </button>
              </div>

              {/* Which subjects actually have material in this state. Grey
                  means nothing is seeded — click it to start building it. */}
              <div className="lb-subject-chips">
                {state.subjectTotals.map((subject) => (
                  <button
                    type="button"
                    key={subject.value}
                    className={`lb-chip lb-chip-${subject.value} ${
                      subject.has ? "is-on" : "is-off"
                    }`}
                    onClick={() => startPageForState(state.key, subject.value)}
                    title={
                      subject.has
                        ? `${subject.topics} topics across ${subject.grades} grade(s) — click to build another`
                        : `No ${subject.label} material in ${state.label} yet — click to start one`
                    }
                  >
                    <span className="lb-chip-name">{subject.label}</span>
                    <span className="lb-chip-count">
                      {subject.has ? subject.topics : "—"}
                    </span>
                  </button>
                ))}
              </div>

              {stateOpen && state.groups.length === 0 && (
                <div className="lb-acc-body">
                  <p className="lb-empty lb-empty-sm">
                    Nothing built for {state.label} yet. This state is offered
                    at signup, so anyone registering here lands on empty
                    subjects until a page is saved.
                  </p>
                </div>
              )}

              {stateOpen && (
                <div className="lb-acc-body">
                  {state.groups.map((group) => {
                    const groupOpen = Boolean(openGroups[group.key]);

                    return (
                      <div
                        className={`lb-group lb-subject-${group.subject}`}
                        key={group.key}
                      >
                        <button
                          type="button"
                          className="lb-group-head"
                          onClick={() => toggleGroup(group.key)}
                          aria-expanded={groupOpen}
                        >
                          <Chevron open={groupOpen} />
                          <span className="lb-group-title">
                            {group.grade} · {subjectLabel(group.subject)}
                          </span>
                          <span className="lb-count">
                            {group.topicCount} topics
                          </span>
                        </button>

                        {groupOpen && (
                          <div className="lb-group-body">
                            {group.layouts.map((item) => (
                              <div className="lb-layout" key={item.id}>
                                <div className="lb-layout-head">
                                  <div>
                                    <h3>{item.title || "Untitled page"}</h3>
                                    <code className="lb-layout-id">
                                      {item.id}
                                    </code>
                                  </div>

                                  <div className="lb-layout-actions">
                                    <span
                                      className={`lb-pill lb-pill-${item.view}`}
                                    >
                                      {item.view === "student"
                                        ? "Student"
                                        : "Educator"}
                                    </span>
                                    <button
                                      type="button"
                                      className="lb-btn lb-btn-sm"
                                      onClick={() => openLayoutFromLibrary(item)}
                                    >
                                      Open
                                    </button>
                                    <button
                                      type="button"
                                      className="lb-btn lb-btn-ghost lb-btn-sm"
                                      onClick={() =>
                                        navigate(
                                          `/portal/${item.state}/${item.grade}/${item.subject}/${item.view}`
                                        )
                                      }
                                    >
                                      Preview
                                    </button>
                                    <button
                                      type="button"
                                      className="lb-btn lb-btn-danger lb-btn-sm"
                                      onClick={() => handleDeleteLayout(item.id)}
                                    >
                                      Delete page
                                    </button>
                                  </div>
                                </div>

                                {(item.topics || []).length === 0 ? (
                                  <p className="lb-empty lb-empty-sm">
                                    No topics on this page yet.
                                  </p>
                                ) : (
                                  <ul className="lb-layout-topics">
                                    {item.topics.map((topic, i) => (
                                      <li
                                        key={`${topic.slug || "t"}-${i}`}
                                        className="lb-layout-topic"
                                      >
                                        <span className="lb-layout-topic-order">
                                          {i + 1}
                                        </span>
                                        <span className="lb-layout-topic-name">
                                          {topic.title || "Untitled topic"}
                                          {topic.visible === false && (
                                            <span className="lb-pill lb-pill-muted">
                                              Hidden
                                            </span>
                                          )}
                                        </span>
                                        <MediaDots topic={topic} />
                                        <button
                                          type="button"
                                          className="lb-btn lb-btn-soft lb-btn-sm"
                                          onClick={() =>
                                            openTopicFromLibrary(
                                              item,
                                              topic.slug
                                            )
                                          }
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="lb-btn lb-btn-danger lb-btn-sm"
                                          onClick={() =>
                                            deleteTopicFromLayout(
                                              item,
                                              topic.slug,
                                              topic.title
                                            )
                                          }
                                        >
                                          Delete
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}

                                <button
                                  type="button"
                                  className="lb-btn lb-btn-soft lb-btn-sm"
                                  onClick={() =>
                                    addTopicToLayoutFromLibrary(item)
                                  }
                                >
                                  + Add a topic to this page
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <div className="lb-savebar">
        <div className="lb-savebar-info">
          {dirty ? (
            <span className="lb-dirty">Unsaved changes</span>
          ) : (
            <span className="lb-clean">All changes saved</span>
          )}
          <code>{layoutId}</code>
          {mirrorViews && (
            <>
              {" + "}
              <code>{siblingLayoutId}</code>
            </>
          )}
        </div>

        {/* Sits before Save so the destructive option is not the one under
            the thumb, and is disabled when there is nothing to discard. */}
        <button
          type="button"
          className="lb-btn lb-btn-cancel"
          onClick={handleCancelEdits}
          disabled={saving || !dirty}
          title={
            dirty
              ? "Throw away unsaved changes and reload the saved page"
              : "Nothing unsaved to discard"
          }
        >
          Cancel changes
        </button>

        <button
          type="button"
          className="lb-btn lb-btn-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? "Saving…"
            : mirrorViews
            ? "Save to both pages"
            : `Save ${form.view} page only`}
        </button>
      </div>
    </div>
  );
};

export default LayoutBuilder;
